'use client';

import classNames from 'classnames';
import { AiFillCloseCircle } from 'react-icons/ai';
import Select, {
  ClearIndicatorProps,
  MultiValueRemoveProps,
  Props,
  components,
} from 'react-select';
import { VariantProps } from 'tailwind-variants';
import { Flex } from '../../../../layout';
import { Text } from '../../../../typography';
import { IsVisible } from '../../../../utils';
import { multiSelectStyles } from './styles';

interface IComponentSelectFieldOption<T = string | number | boolean> {
  isVisible?: boolean;
  label: string;
  value: T;
}

interface IGroupedOptionsFormatted {
  label: string;
  options: IComponentSelectFieldOption[];
}

export interface IMultiSelectFieldProps
  extends Props,
    VariantProps<typeof multiSelectStyles.container> {
  disabled?: boolean;
  error: string | undefined;
  groupedOptionsFormatted?: IGroupedOptionsFormatted[];
  label?: string;
  menuPlacement?: 'auto' | 'bottom' | 'top';
}

function ClearIndicator(props: ClearIndicatorProps) {
  return (
    <components.ClearIndicator {...props}>
      <AiFillCloseCircle />
    </components.ClearIndicator>
  );
}

function MultiValueRemove(props: MultiValueRemoveProps) {
  return (
    <components.MultiValueRemove {...props}>
      <AiFillCloseCircle />
    </components.MultiValueRemove>
  );
}

export function MultiSelectField({
  design,
  disabled,
  error,
  groupedOptionsFormatted,
  id,
  label,
  menuPlacement,
  options,
  value,
  ...props
}: IMultiSelectFieldProps) {
  const formatGroupLabel = (data: any) => (
    <Flex className="border-b-2 border-b-secondary-two py-1 items-center justify-between">
      <Text className="text-secondary-two font-semibold" variant="small">
        {`⁍ ${data.label}`}
      </Text>
      <span className="bg-[#EBECF0] font-semibold rounded-full text-[#172B4D] text-[12px] leading-none min-w-[1px] px-2 py-[0.1666667em] text-center">
        {data.options.length}
      </span>
    </Flex>
  );
  return (
    <div
      className={classNames('relative', {
        'cursor-not-allowed': disabled,
      })}
    >
      <IsVisible when={!!label}>
        <Text
          className={multiSelectStyles.label({ design, errored: !!error })}
          variant="label"
        >
          {label}
        </Text>
      </IsVisible>
      <Select
        classNames={{
          container: () => multiSelectStyles.container({ design }),
          control: ({ isFocused }) =>
            multiSelectStyles.control({ design, focus: isFocused }),
          placeholder: () => multiSelectStyles.placeholder({ design }),
          input: () => multiSelectStyles.input({ design }),
          valueContainer: () => multiSelectStyles.valueContainer({ design }),
          singleValue: () => multiSelectStyles.singleValue({ design }),
          multiValue: () => multiSelectStyles.multiValue({ design }),
          multiValueLabel: () => multiSelectStyles.multiValueLabel({ design }),
          multiValueRemove: () =>
            multiSelectStyles.multiValueRemove({ design }),
          indicatorsContainer: () => multiSelectStyles.indicators({ design }),
          clearIndicator: () => multiSelectStyles.clearIndicator({ design }),
          indicatorSeparator: () =>
            multiSelectStyles.indicatorSeparator({ design }),
          dropdownIndicator: () =>
            multiSelectStyles.dropdownIndicator({ design }),
          menu: () => multiSelectStyles.menu({ design }),
          groupHeading: () => multiSelectStyles.groupHeading({ design }),
          option: ({ isFocused, isSelected }) =>
            multiSelectStyles.option({
              design,
              focus: isFocused,
              selected: isSelected,
            }),
          noOptionsMessage: () =>
            multiSelectStyles.noOptionsMessage({ design }),
        }}
        closeMenuOnSelect={false}
        components={{ ClearIndicator, MultiValueRemove }}
        formatGroupLabel={formatGroupLabel}
        hideSelectedOptions={false}
        id={`select-${id}`}
        inputId={`search-input-${id}`}
        instanceId={`select-${id}`}
        isDisabled={disabled}
        isMulti
        menuPlacement={menuPlacement || 'auto'}
        menuShouldScrollIntoView={false}
        options={groupedOptionsFormatted || options}
        placeholder={props.placeholder || 'Select one or more options'}
        styles={{
          container: base => ({
            ...base,
            position: 'relative',
            zIndex: '9999',
            width: '100%',
          }),
          input: base => ({
            ...base,
            'input:focus': {
              boxShadow: 'none',
            },
          }),
          menuPortal: base => ({ ...base, zIndex: 9999 }),
          multiValueLabel: base => ({
            ...base,
            whiteSpace: 'normal',
            overflow: 'visible',
          }),
          control: base => ({
            ...base,
            transition: 'none',
            minHeight: '0',
          }),
        }}
        unstyled
        value={value}
        {...props}
      />
      <IsVisible when={!!error}>
        <div className="flex mt-1 ml-auto w-fit">
          <span className="text-primary-two text-xs">{error}</span>
        </div>
      </IsVisible>
    </div>
  );
}
